import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';
import {
  RowListGrip,
  RowListRemove,
  RowListRow,
  type RowListRowProps
} from './RowList';
import { useSortableRow } from './SortableRows';

export type RowListSortableRowProps = Omit<
  RowListRowProps,
  'role' | 'aria-label' | 'children'
> & {
  /** Id in the enclosing `SortableRows`. */
  id: string;
  /** Names the row for its handle and ✕ ("Language 2"). */
  label: string;
  /** Two rows or more: the row has a handle. */
  sortable: boolean;
  onRemove(): void;
  /** The row's parts: `RowListInput`, `RowListPart`s. */
  children: ReactNode;
};

/**
 * One row of `SortableRows`: its handle first (kept, disabled and invisible,
 * with a single row, so a second row moves nothing), then the parts it is
 * given, then ✕. Parts for the lead column go before the value.
 */
export function RowListSortableRow({
  id,
  label,
  sortable,
  onRemove,
  children,
  ...row
}: RowListSortableRowProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'RowList' });
  const { rowProps, handleProps, isDragging } = useSortableRow(id, sortable);
  return (
    <RowListRow
      {...row}
      {...rowProps}
      role="listitem"
      aria-label={label}
      className={cn(isDragging && 'bg-bg-default shadow-md')}
    >
      <RowListGrip
        {...handleProps}
        disabled={!sortable}
        aria-label={t('reorder', { label })}
      />
      {children}
      <RowListRemove
        type="button"
        aria-label={t('remove', { label })}
        onClick={onRemove}
      />
    </RowListRow>
  );
}
